/** The one place that decides which voice-profile controls a user may see.
 *
 * Three independent gates have to agree, and the issue is explicit that they
 * are not interchangeable:
 *   - the feature itself (deployment readiness),
 *   - the user's role (contributors may assign a person; only organization
 *     admins may save, inspect or delete biometric samples),
 *   - the surface (a read-only pane or a shared link never exposes
 *     enrollment).
 *
 * Assigning a person stays available when voice profiles are switched off,
 * because ordinary directory assignment is not biometric enrollment. Only
 * enrollment and recognition depend on deployment readiness. */

import { useMemo } from 'react';
import { VOICE_PROFILES_UI_ENABLED } from '@utils/constants/app-constants.ts';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { useVoiceProfileCapabilities } from '@utils/api/organizations/speakers/voice-profiles';
import { VoiceProfileCapabilities } from '@utils/types/organization/voice-profile.ts';
import { Session } from '@utils/types/editors/sessions.ts';

export interface VoiceProfileFeature {
  /** Still loading readiness; hide controls rather than flashing them. */
  isLoading: boolean;
  capabilities: VoiceProfileCapabilities | undefined;
  /** Feature on for this organization. */
  enabled: boolean;
  /** May record identity decisions (contributor or admin, writable surface). */
  canAssign: boolean;
  /** May save, inspect or delete voice samples. */
  canEnroll: boolean;
  /** Recognition suggestions are meaningful for this organization. */
  canReviewSuggestions: boolean;
  /** Whether identity data is worth fetching before the user asks for it.
   *
   *  True only where a suggestion could be waiting to be reviewed, which is
   *  the one case the editor has to show unprompted. Everywhere else the
   *  timeline and identification list are fetched when the user actually opens
   *  a voice control, so a document in an organization that never uses the
   *  feature costs nothing beyond the one capabilities request. */
  eagerIdentity: boolean;
  /** Source media is still retained and the session finished, so the
   *  server can cut a sample. Otherwise enrollment is explained, not offered. */
  sourceAvailable: boolean;
  /** Sample-length bounds, straight from the backend's own limits. */
  minSampleSeconds: number;
  maxSampleSeconds: number;
  maxSamplesPerSpeaker: number;
}

export interface VoiceProfileFeatureOptions {
  /** The editor's session, for retention and finished-status checks. */
  session?: Session;
  /** True in a read-only pane (split-view source, finished document view). */
  readOnly?: boolean;
  /** True in a shared-link editor, which never exposes enrollment. */
  isShared?: boolean;
}

/** Fallbacks matching the backend's own constants, used only before the
 *  capabilities response lands so the dialog never renders a NaN limit. */
const DEFAULT_MIN_SECONDS = 10;
const DEFAULT_MAX_SECONDS = 60;
const DEFAULT_MAX_SAMPLES = 20;

export const useVoiceProfileFeature = ({
  session,
  readOnly = false,
  isShared = false
}: VoiceProfileFeatureOptions = {}): VoiceProfileFeature => {
  const { isAdmin, isContributor, isSysOp } = useRoles();

  // A shared-link viewer has no organization context to ask about, so skip
  // the request entirely instead of sending one that cannot help.
  const { data: capabilities, isLoading } =
    useVoiceProfileCapabilities(!isShared);

  return useMemo(() => {
    const enabled = VOICE_PROFILES_UI_ENABLED && !!capabilities?.enabled;
    const writable = VOICE_PROFILES_UI_ENABLED && !readOnly && !isShared;

    // The backend's `can_enroll` already folds in the admin scope and the
    // operator's configuration. Re-checking the role here keeps the control
    // hidden on surfaces where it must never appear even for an admin.
    const canEnroll = !!capabilities?.can_enroll && writable;

    // Contributors and admins may record voice-identity decisions. This is not
    // enrollment, needs no admin scope, and deliberately does NOT require the
    // deployment switch: the issue is explicit that ordinary directory
    // assignment stays available when voice profiles are disabled, and the
    // backend's assignment route accepts it without one.
    //
    // The cost that would otherwise come with that — every document open in
    // every organization fetching a timeline and an identification list — is
    // handled by `eagerIdentity` instead: the data is fetched when the user
    // opens a voice control, not on the chance that they might.
    const canAssign = writable && !!(isAdmin || isContributor || isSysOp);

    const status = session?.status?.toLowerCase();
    // `keep_media` is undefined on responses that omit it; treat only an
    // explicit false as "not retained" so the action is not hidden wrongly.
    const retained = session?.keep_media !== false;
    const sourceAvailable = status === 'finished' && retained;

    const canReviewSuggestions =
      VOICE_PROFILES_UI_ENABLED &&
      !!capabilities?.recognition_available &&
      !isShared;

    return {
      isLoading,
      capabilities,
      enabled,
      canAssign,
      canEnroll,
      canReviewSuggestions,
      // A pending suggestion has to appear without being asked for — and so
      // does a decision a human already made. The chip renders from the
      // identification record, so fetching only when recognition is
      // calibrated meant that on a deployment without thresholds an explicit
      // assignment vanished from the row on reload: stored, but never read
      // back. Organizations that do not have the feature still fetch nothing,
      // because `enabled` is the deployment-and-organization switch.
      eagerIdentity: canReviewSuggestions || (enabled && canAssign),
      sourceAvailable,
      minSampleSeconds: capabilities?.min_sample_seconds ?? DEFAULT_MIN_SECONDS,
      maxSampleSeconds: capabilities?.max_sample_seconds ?? DEFAULT_MAX_SECONDS,
      maxSamplesPerSpeaker:
        capabilities?.max_samples_per_speaker ?? DEFAULT_MAX_SAMPLES
    };
  }, [
    capabilities,
    isLoading,
    readOnly,
    isShared,
    isAdmin,
    isContributor,
    isSysOp,
    session?.status,
    session?.keep_media
  ]);
};
